/**
 * Easing functions for wheel animations.
 * t: 0..1 progress, returns eased value 0..1.
 * New easings can be added via registerEasing() without modifying engine.
 */

import { EASING_KEY } from "@/shared/constants/pvpRoulette"

export type EasingFn = (t: number) => number

const linear: EasingFn = (t) => t

const easeOutCubic: EasingFn = (t) => 1 - Math.pow(1 - t, 3)

const easeInOutCubic: EasingFn = (t) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2

const easeOutQuint: EasingFn = (t) => 1 - Math.pow(1 - t, 5)

/** Spin-out: fast start, slow finish (deceleration). easeOutQuad. */
const spinOut: EasingFn = (t) => 1 - Math.pow(1 - t, 2)

/** UI-fast: snappy, slight overshoot feel */
const uiFast: EasingFn = (t) => t * (2 - t)

/** Default presets. Extend via registerEasing(). */
export const EASING_PRESETS: Record<string, EasingFn> = {
  [EASING_KEY.LINEAR]: linear,
  [EASING_KEY.EASE_OUT_CUBIC]: easeOutCubic,
  [EASING_KEY.EASE_IN_OUT_CUBIC]: easeInOutCubic,
  [EASING_KEY.EASE_OUT_QUINT]: easeOutQuint,
  [EASING_KEY.SPIN_OUT]: spinOut,
  [EASING_KEY.SPIN_IN]: easeOutCubic,
  [EASING_KEY.UI_FAST]: uiFast,
}

/** Custom easings (designer can add without editing engine). */
const custom: Record<string, EasingFn> = {}

/**
 * Register a custom easing. Use for design overrides without modifying engine.
 */
export function registerEasing(key: string, fn: EasingFn): void {
  custom[key] = fn
}

/**
 * Get easing by key. Fallback: linear.
 */
export function getEasing(key: string): EasingFn {
  return custom[key] ?? EASING_PRESETS[key] ?? linear
}
